/**
 * SessionsListModal Component
 * 
 * Modal for displaying and managing active user sessions
 * 
 * Features:
 * - Modal overlay with focus trap
 * - List of active sessions with device/location info
 * - Expandable detail view for IP address
 * - Revoke session functionality
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, Shield, Trash2, ChevronDown, ChevronUp, Loader2, AlertCircle } from 'lucide-react';
import { securityService } from '../../../services/security-service';
import { getCurrentUserId } from '../../../lib/supabase';
import type { SessionInfo } from '../../../types/security.types';

interface SessionsListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionRevoked?: () => void;
}

export function SessionsListModal({ 
  isOpen, 
  onClose,
  onSessionRevoked
}: SessionsListModalProps): React.ReactElement | null {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [revokingSessionId, setRevokingSessionId] = useState<string | null>(null);

  // Load sessions when modal opens
  useEffect(() => {
    if (isOpen) {
      loadSessions();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSessions([]);
      setError('');
      setExpandedSessions(new Set());
      setRevokingSessionId(null);
    }
  }, [isOpen]);

  // Focus trap and keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the first focusable element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, loading, onClose]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const userId = await getCurrentUserId();
      if (!userId) {
        setError('You must be logged in to view sessions');
        return;
      }

      const response = await securityService.getActiveSessions(userId);
      
      if (response.error) {
        setError(response.error.message || 'Failed to load sessions');
        return;
      }

      if (response.data) {
        setSessions(response.data.sessions);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (sessionId: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  const handleRevokeSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to revoke this session?')) {
      return;
    }

    try {
      setRevokingSessionId(sessionId);
      setError('');
      
      const userId = await getCurrentUserId();
      if (!userId) {
        setError('You must be logged in to revoke sessions');
        return;
      }

      const response = await securityService.revokeSession(userId, sessionId);
      
      if (response.error) {
        setError(response.error.message || 'Failed to revoke session');
        return;
      }

      // Remove session from list
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      
      // Notify parent component
      if (onSessionRevoked) {
        onSessionRevoked();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to revoke session');
    } finally {
      setRevokingSessionId(null);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const formatLastActive = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sessions-list-title"
      aria-describedby="sessions-list-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900 max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
                <Shield className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              </div>
              <div>
                <h2 id="sessions-list-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Active Sessions
                </h2>
                <p id="sessions-list-description" className="text-sm text-gray-600 dark:text-gray-400">
                  Manage your active login sessions
                </p>
              </div>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading && sessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                  Loading sessions...
                </p>
              </div>
            ) : error ? (
              <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No active sessions found
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => {
                  const isExpanded = expandedSessions.has(session.id);
                  const isRevoking = revokingSessionId === session.id;

                  return (
                    <div
                      key={session.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h3 className="font-medium text-gray-900 dark:text-gray-100">
                                {session.deviceType}
                              </h3>
                              {session.isCurrentSession && (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                                  This device
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                              {session.location}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                              Last active: {formatLastActive(session.lastActiveAt)}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {session.ipAddress && (
                              <button
                                type="button"
                                onClick={() => toggleExpand(session.id)}
                                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                aria-label={isExpanded ? 'Hide details' : 'Show details'}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="h-5 w-5" />
                                ) : (
                                  <ChevronDown className="h-5 w-5" />
                                )}
                              </button>
                            )}
                            {!session.isCurrentSession && (
                              <button
                                type="button"
                                onClick={() => handleRevokeSession(session.id)}
                                disabled={isRevoking}
                                className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                                aria-label="Revoke session"
                              >
                                {isRevoking ? (
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                  <Trash2 className="h-5 w-5" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {isExpanded && session.ipAddress && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              <span className="font-medium">IP Address:</span> {session.ipAddress}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex justify-end">
              <button 
                type="button" 
                onClick={onClose}
                className="btn-secondary btn-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

